import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
} from "@mui/material";
import axios from "axios";
import { backendUrl } from "../utils/urlHelper";
import { useNavigate } from "react-router-dom";

const ViewEnquiryListWeb = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const token = localStorage.getItem("token");
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : {};

  const enquiriesPerPage = 50;

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "manager") {
      fetchEnquiries(
        backendUrl + `/api/enquiries`,
        currentPage,
        enquiriesPerPage
      );
    } else if (user?.role === "staff") {
      fetchEnquiries(
        backendUrl + `/api/teacher/enquiries`,
        currentPage,
        enquiriesPerPage
      );
    }
  }, [currentPage, enquiriesPerPage]);

  const fetchEnquiries = async (endpoint, page, limit) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${endpoint}?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: token },
        }
      );

      const { enquiries: fetchedEnquiries, pages } = response.data;
      setEnquiries(fetchedEnquiries || []);
      setTotalPages(pages || 1);
    } catch (error) {
      console.error("Failed to fetch enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  // Open Dialog with Selected Enquiry
  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDialog(true);
  };

  // Close Dialog
  const handleCloseDialog = () => {
    setSelectedEnquiry(null);
    setShowDialog(false);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const downloadEnquiries = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/enquiries/download", {
        headers: { Authorization: token },
        responseType: "blob", // Important for file downloads
      });

      // Create a link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "enquiries.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error("Failed to download enquiries:", error);
    }
  };

  const filteredEnquiries = enquiries.filter((enquiry) => {
    const query = searchQuery.toLowerCase();

    return (
      enquiry?.studentInfo?.fullName?.toLowerCase().includes(query) ||
      String(enquiry?.studentInfo?.currentGrade || "")
        .toLowerCase()
        .includes(query)
    );
  });

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h5" sx={{ marginBottom: 3, textAlign: "center" }}>
        {user.role === "admin" ? "All Enquiries" : "Your Enquiries"}
      </Typography>

      {/* Search Bar */}
      <TextField
        label="Search by Student Name or Grade (Current page only)"
        variant="outlined"
        fullWidth
        sx={{ mb: 1 }}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          mt: 1,
          mb: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={() => navigate("/enquiry/view")}
          sx={{ textTransform: "none" }}
        >
          Change Status or Add Follow-up
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={downloadEnquiries}
          sx={{ textTransform: "none" }}
        >
          Download as Excel
        </Button>
      </Box>

      {/* Loader */}
      {loading && (
        <Typography textAlign="center" variant="body1">
          Loading enquiries...
        </Typography>
      )}

      {/* Table of Enquiries */}
      {!loading && filteredEnquiries.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Student Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Grade</strong>
                </TableCell>
                <TableCell>
                  <strong>School</strong>
                </TableCell>
                <TableCell>
                  <strong>Reference Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Lead Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((enquiry, index) => (
                <TableRow key={index}>
                  <TableCell>{enquiry.studentInfo.fullName}</TableCell>
                  <TableCell>{enquiry.status}</TableCell>
                  <TableCell>{enquiry.studentInfo.currentGrade}</TableCell>
                  <TableCell>
                    {enquiry.studentInfo.currentSchool || "N/A"}
                  </TableCell>
                  <TableCell>
                    {enquiry.marketing.referenceType || "N/A"}
                  </TableCell>
                  <TableCell>{enquiry.marketing.leadType || "N/A"}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDetails(enquiry)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        !loading && (
          <Typography textAlign="center" variant="body1">
            No matching enquiries found on this page. Try checking other pages.
          </Typography>
        )
      )}

      {/* Pagination */}
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handlePageChange}
        sx={{ marginTop: 3, display: "flex", justifyContent: "center" }}
      />

      {/* Enquiry Details Dialog */}
      {selectedEnquiry && (
        <Dialog
          open={showDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Enquiry Details</DialogTitle>
          <DialogContent>
            <Typography variant="h6">Student Info</Typography>
            <Typography>
              <strong>Name:</strong> {selectedEnquiry.studentInfo.fullName}
            </Typography>
            <Typography>
              <strong>Grade:</strong> {selectedEnquiry.studentInfo.currentGrade}
            </Typography>
            <Typography>
              <strong>School:</strong>{" "}
              {selectedEnquiry.studentInfo.currentSchool || "N/A"}
            </Typography>

            <Typography variant="h6" mt={2}>
              Parents/Guardians
            </Typography>
            {selectedEnquiry.parents.map((parent, index) => (
              <Box key={index} sx={{ marginBottom: 2 }}>
                <Typography>
                  <strong>Relationship:</strong> {parent.relationship}
                </Typography>
                <Typography>
                  <strong>Name:</strong> {parent.name}
                </Typography>
                <Typography>
                  <strong>Mobile:</strong> {parent.mobile}
                </Typography>
                <Typography>
                  <strong>Occupation:</strong> {parent.occupation || "N/A"}
                </Typography>
              </Box>
            ))}

            {/* Sibling Info */}
            {selectedEnquiry.siblings.length > 0 && (
              <>
                <Typography variant="h6" mt={2}>
                  Sibling Details
                </Typography>
                {selectedEnquiry.siblings.map((sibling, index) => (
                  <Box key={index} sx={{ marginBottom: 2 }}>
                    <Typography>
                      <strong>Name:</strong> {sibling.name}
                    </Typography>
                    <Typography>
                      <strong>Grade:</strong> {sibling.grade}
                    </Typography>
                    <Typography>
                      <strong>School:</strong> {sibling.school || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            <Typography variant="h6" mt={2}>
              Location
            </Typography>
            <Typography>
              <strong>Address:</strong> {selectedEnquiry.location.address}
            </Typography>
            <Typography>
              <strong>Village:</strong> {selectedEnquiry.location.village}
            </Typography>
            <Typography>
              <strong>GPS:</strong>{" "}
              {selectedEnquiry?.location?.coordinates?.latitude ? (
                <a
                  href={`https://maps.google.com/?q=${selectedEnquiry?.location?.coordinates?.latitude},${selectedEnquiry?.location?.coordinates?.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1a73e8",
                    textDecoration: "underline",
                    marginLeft: 2,
                  }}
                >
                  Open in Maps
                </a>
              ) : (
                "Not found"
              )}
            </Typography>

            <Typography variant="h6" mt={2}>
              Enquiry Info
            </Typography>
            <Typography>
              <strong>Reference Type:</strong>{" "}
              {selectedEnquiry.marketing.referenceType}
            </Typography>
            <Typography>
              <strong>Lead Type:</strong> {selectedEnquiry.marketing.leadType}
            </Typography>

            {/* Sibling Info */}
            {selectedEnquiry.teacherInfo?.teacherName && (
              <>
                <Typography variant="h6" mt={2}>
                  Teacher Info
                </Typography>
                <Typography>
                  <strong>Name:</strong>{" "}
                  {selectedEnquiry?.teacherInfo?.teacherName}
                </Typography>
                <Typography>
                  <strong>Branch:</strong>{" "}
                  {selectedEnquiry?.teacherInfo?.teacherBranch}
                </Typography>
              </>
            )}

            <Typography variant="h6" mt={2}>
              Other Info
            </Typography>
            <Typography>
              <strong>For AY:</strong> {selectedEnquiry?.academicYear}
            </Typography>
            <Typography>
              <strong>For Branch:</strong> {selectedEnquiry?.branch}
            </Typography>

            {selectedEnquiry.statusHistory &&
              selectedEnquiry.statusHistory.length > 0 && (
                <Divider sx={{ my: 2 }} />
              )}

            {/* ---------- Status History ---------- */}
            {selectedEnquiry.statusHistory &&
              selectedEnquiry.statusHistory.length > 0 && (
                <>
                  <Typography variant="h6" mt={2}>
                    Status Change History
                  </Typography>
                  {selectedEnquiry.statusHistory.map((history, idx) => (
                    <Box key={idx} sx={{ marginBottom: 2 }}>
                      <Typography>
                        <strong>Changed By:</strong>{" "}
                        {history.changedByName || "N/A"}
                      </Typography>
                      <Typography>
                        <strong>When:</strong>{" "}
                        {new Date(history.changedAt).toLocaleString()}
                      </Typography>
                      <Typography>
                        <strong>From:</strong> {history.fromStatus}
                      </Typography>
                      <Typography>
                        <strong>To:</strong> {history.toStatus}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

            {selectedEnquiry.followUps &&
              selectedEnquiry.followUps.length > 0 && (
                <Divider sx={{ my: 2 }} />
              )}

            {/* ---------- Follow-Up History ---------- */}
            {selectedEnquiry.followUps &&
              selectedEnquiry.followUps.length > 0 && (
                <>
                  <Typography variant="h6" mt={2}>
                    Follow-up History
                  </Typography>
                  {selectedEnquiry.followUps.map((fu, index) => (
                    <Box key={index} sx={{ marginBottom: 2 }}>
                      <Typography>
                        <strong>By:</strong> {fu.followUpByName || "Unknown"}
                      </Typography>
                      <Typography>
                        <strong>Date:</strong>{" "}
                        {new Date(fu.createdAt).toLocaleString()}
                      </Typography>
                      <Typography>
                        <strong>Note:</strong> {fu.note}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default ViewEnquiryListWeb;
